import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import moment from 'moment'
import React from 'react'
import { i18n } from '../../index/index'
import { MonthlyActualValue, MonthlyTargetValue } from './types'
import { useValueFormat } from './valueFormat'
import { deriveMonthlyRows } from './deriveMonthlyRows'
import { MonthlyTargetRow } from './MonthlyTargetRow'

export interface MonthlyTargetsTableProps {
  months: number[]
  targetYear: number
  monthlyTargets: MonthlyTargetValue[]
  lastYearActuals?: MonthlyActualValue[]
  isEditing: boolean
  unallocated: number
  focusedMonth: number | null
  onFocusMonth: (month: number | null) => void
  changeMonthlyTarget: (month: number, value: number) => void
  fillRemainderIntoMonth: (month: number) => void
}

/**
 * Vertical monthly table (desktop Figma): one row per month with columns
 * Kuukausi · last-year actual · editable target · Ero (delta vs last year),
 * plus a Yhteensä total row. The current month row is highlighted; the "Lisää loput tähän"
 * hint follows the focused cell when the months are under-allocated (see MonthlyTargetRow).
 */
export function MonthlyTargetsTable({
  months,
  targetYear,
  monthlyTargets,
  lastYearActuals,
  isEditing,
  unallocated,
  focusedMonth,
  onFocusMonth,
  changeMonthlyTarget,
  fillRemainderIntoMonth,
}: MonthlyTargetsTableProps) {
  const { format, formatSigned } = useValueFormat()
  const lastYear = targetYear - 1
  const {
    rows,
    hasLastYear,
    targetSum,
    lastYearSum,
    comparableMonthCount,
    comparableTargetSum,
    comparableLastYearSum,
  } = deriveMonthlyRows(months, monthlyTargets, lastYearActuals, targetYear)

  return (
    <>
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t('admin.monthlyTargets.monthColumn')}</TableCell>
              <TableCell align='right'>{i18n.t('admin.monthlyTargets.lastYearColumn', { year: lastYear })}</TableCell>
              <TableCell align='right'>{i18n.t('admin.monthlyTargets.targetColumn', { year: targetYear })}</TableCell>
              <TableCell align='right'>{i18n.t('admin.monthlyTargets.differenceColumn', { year: lastYear })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <MonthlyTargetRow
                key={row.month}
                month={row.month}
                monthlyTarget={row.monthlyTarget}
                lastYearOutcome={row.lastYearOutcome}
                hasLastYear={hasLastYear}
                isCurrentMonth={row.isCurrentMonth}
                isEditing={isEditing && !row.isLocked}
                unallocated={unallocated}
                isFocused={focusedMonth === row.month}
                onFocusMonth={onFocusMonth}
                changeMonthlyTarget={changeMonthlyTarget}
                fillRemainderIntoMonth={fillRemainderIntoMonth}
              />
            ))}
            <TableRow sx={{ '& > td': { height: 40 } }}>
              <TableCell>
                <strong>{i18n.t('admin.monthlyTargets.totalRow')}</strong>
              </TableCell>
              <TableCell align='right'>{hasLastYear ? format(lastYearSum) : '-'}</TableCell>
              <TableCell align='right'>
                <strong>{format(targetSum)}</strong>
              </TableCell>
              <TableCell align='right'>
                {hasLastYear && comparableMonthCount > 0
                  ? formatSigned(comparableTargetSum - comparableLastYearSum)
                  : '-'}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

export function monthName(month: number): string {
  return moment()
    .locale('fi')
    .month(month - 1)
    .format('MMMM')
}
